import { Button, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'

import agent from 'agent'
import { ButtonWithConfirmation } from 'components/common/ButtonWithConfirmation'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import Icon from 'components/Icon'
import { AWSAccountIdDrawer } from 'components/rbac-settings/teams/AWSAccountIdDrawer'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import toast from 'lib/toast'
import { messageFromResponseError } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

import type { GridCellParams } from '@mui/x-data-grid'
import type { TeamExternalIntegration } from 'types/api/status-checks/status-check'

export const TeamIntegrationTableActions: React.FC<{
  isAwsIntegrationsTable?: boolean
  onChange: () => void
  params: GridCellParams<TeamExternalIntegration>
  savedIntegrations: TeamExternalIntegration[]
}> = ({ isAwsIntegrationsTable, onChange, params, savedIntegrations }) => {
  const { evaluatedTeamId } = useStatusCheckExternalIntegrationsContext()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const statusChecksUrls = useGetStatusChecksUrls()

  const handleDelete = async () => {
    const integration = params.row

    // Special case for Datadog US1 integration. The domain is not required for this integration type.
    const domainName =
      integration.type === IntegrationType.Datadog ||
      integration.domain === null
        ? ''
        : integration.domain

    // The "domain" query param cannot be null or undefined. The backend API will attempt to parse that value into a string
    // and the API call will fail.
    const apiUrl = `external-integrations/status-check?teamId=${evaluatedTeamId}&observabilityToolType=${integration.observabilityToolType}&domain=${domainName}&name=${integration.name}`

    try {
      setIsSubmitting(true)
      await agent.delete(apiUrl)
      onChange()

      toast({
        type: 'success',
        text: 'Successfully revoked Authentication'
      })
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was a problem while removing the team integration. Please try again.'
        )
      })
      setIsSubmitting(false)
    }
  }

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      {isAwsIntegrationsTable ? (
        <AWSAccountIdDrawer
          buttonIcon="edit"
          buttonText="Edit"
          externalIntegrations={savedIntegrations}
          selectedAWSAccount={params.row}
        />
      ) : (
        <Button
          component={StyledRouterLink}
          data-test-edit-status-check-authentication={params.id}
          data-track-action="edit-status-check-authentication"
          data-track-category="status-check-authentication"
          data-track-on="click"
          size="small"
          startIcon={<Icon icon="edit" />}
          to={statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
            params.row
          )}
          variant="contained"
        >
          Edit
        </Button>
      )}
      <ButtonWithConfirmation
        buttonConfirmProps={{
          color: 'error',
          disabled: isSubmitting,
          startIcon: <Icon icon="trash" />
        }}
        buttonConfirmText="Revoke"
        color="error"
        dialogBodyContent={
          <Typography>
            Are you sure you want to revoke this authentication? This will
            revoke the authentication on this team.
          </Typography>
        }
        dialogTitleContent="Revoke Authentication?"
        size="small"
        startIcon={<Icon icon="trash" />}
        variant="outlined"
        onConfirm={handleDelete}
      >
        Revoke
      </ButtonWithConfirmation>
    </Stack>
  )
}
